import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { DashboardLayout } from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  Brain, 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  Eye,
  FileText,
  Settings,
  TrendingUp,
  Lock,
  Unlock,
  AlertCircle,
  Search,
  Download,
  RefreshCw,
  Server,
  Copy,
  Terminal,
  Bell,
  Mail,
  ToggleLeft,
  ToggleRight
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AIGovernance({ user, logout, token }) {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [tools, setTools] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [complianceReport, setComplianceReport] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Log analysis state
  const [logContent, setLogContent] = useState("");
  const [logAnalysisResult, setLogAnalysisResult] = useState(null);
  const [analyzingLogs, setAnalyzingLogs] = useState(false);
  
  // Sensitive data check state
  const [textToCheck, setTextToCheck] = useState("");
  const [sensitiveDataResult, setSensitiveDataResult] = useState(null);
  const [checkingData, setCheckingData] = useState(false);

  // Alert settings state
  const [alertSettings, setAlertSettings] = useState({
    email_alerts_enabled: true,
    auto_block_enabled: false,
    alert_on_phi: true,
    alert_on_pii: true,
    alert_on_credentials: true,
    auto_block_ssn: true,
    auto_block_medical_records: true,
    auto_block_credit_cards: true,
    auto_block_api_keys: true,
    alert_email: "",
    alert_threshold: "high"
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [alerts, setAlerts] = useState([]);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchDashboard();
    fetchTools();
    fetchPolicies();
    fetchIncidents();
    fetchAlertSettings();
    fetchAlerts();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ai-governance/dashboard`, { headers });
      setDashboard(response.data);
    } catch (error) {
      console.error("Failed to fetch dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTools = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ai-governance/tools`);
      setTools(response.data.tools);
    } catch (error) {
      console.error("Failed to fetch tools:", error);
    }
  };

  const fetchPolicies = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ai-governance/policies`, { headers });
      setPolicies(response.data.policies);
    } catch (error) {
      console.error("Failed to fetch policies:", error);
    }
  };

  const fetchIncidents = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ai-governance/incidents`, { headers });
      setIncidents(response.data.incidents);
    } catch (error) {
      console.error("Failed to fetch incidents:", error);
    }
  };

  const fetchAlertSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ai-governance/alert-settings`, { headers });
      setAlertSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch alert settings:", error);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ai-governance/alerts`, { headers });
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error("Failed to fetch alerts:", error);
    }
  };

  const saveAlertSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.post(`${BACKEND_URL}/api/ai-governance/alert-settings`, alertSettings, { headers });
      toast.success("Alert settings saved successfully");
    } catch (error) {
      toast.error("Failed to save alert settings");
      console.error("Failed to save settings:", error);
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchComplianceReport = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ai-governance/compliance-report`, { headers });
      setComplianceReport(response.data);
      toast.success("Compliance report generated!");
    } catch (error) {
      toast.error("Failed to generate report");
    }
  };

  const analyzeAILogs = async () => {
    if (!logContent.trim()) {
      toast.error("Please paste log content to analyze");
      return;
    }
    
    setAnalyzingLogs(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/ai-governance/analyze-logs`,
        { log_content: logContent, log_type: "network" },
        { headers }
      );
      setLogAnalysisResult(response.data);
      toast.success(`Detected ${response.data.events_detected} AI tool usages`);
      fetchDashboard();
      fetchIncidents();
    } catch (error) {
      toast.error("Failed to analyze logs");
    } finally {
      setAnalyzingLogs(false);
    }
  };

  const checkSensitiveData = async () => {
    if (!textToCheck.trim()) {
      toast.error("Please enter text to check");
      return;
    }
    
    setCheckingData(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/ai-governance/check-sensitive-data`,
        { text: textToCheck },
        { headers }
      );
      setSensitiveDataResult(response.data);
      if (response.data.sensitive_data_found) {
        toast.warning(`Found ${response.data.total_findings} types of sensitive data!`);
      } else {
        toast.success("No sensitive data detected");
      }
    } catch (error) {
      toast.error("Failed to check data");
    } finally {
      setCheckingData(false);
    }
  };

  const getRiskBadgeColor = (risk) => {
    switch (risk) {
      case "critical": return "bg-red-500/20 text-red-400 border-red-500/30";
      case "high": return "bg-orange-500/20 text-orange-400 border-orange-500/30";
      case "medium": return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
      case "low": return "bg-emerald-500/20 text-emerald-400 border-emerald-500/30";
      default: return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold font-[Chivo]">AI Governance</h1>
            <p className="text-muted-foreground mt-1">
              Monitor, audit, and enforce AI usage policies across your organization
            </p>
          </div>
          <Button onClick={fetchComplianceReport} variant="outline">
            <FileText className="mr-2 h-4 w-4" />
            Generate Report
          </Button>
        </div>

        {/* Stats Cards */}
        {dashboard && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-card border-border">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">AI Events Tracked</p>
                    <p className="text-3xl font-bold">{dashboard.summary.total_ai_events}</p>
                  </div>
                  <Brain className="h-8 w-8 text-primary opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-card border-border">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Policy Violations</p>
                    <p className="text-3xl font-bold text-red-500">{dashboard.summary.policy_violations}</p>
                  </div>
                  <AlertTriangle className="h-8 w-8 text-red-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-card border-border">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Data Incidents</p>
                    <p className="text-3xl font-bold text-orange-500">{dashboard.summary.sensitive_data_incidents}</p>
                  </div>
                  <Lock className="h-8 w-8 text-orange-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-card border-border">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Compliance Score</p>
                    <p className="text-3xl font-bold text-emerald-500">{dashboard.summary.compliance_score}%</p>
                  </div>
                  <Shield className="h-8 w-8 text-emerald-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-card border border-border">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-primary">
              <TrendingUp className="h-4 w-4 mr-2" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="monitor" className="data-[state=active]:bg-primary">
              <Eye className="h-4 w-4 mr-2" />
              Monitor Logs
            </TabsTrigger>
            <TabsTrigger value="data-check" className="data-[state=active]:bg-primary">
              <Search className="h-4 w-4 mr-2" />
              Data Scanner
            </TabsTrigger>
            <TabsTrigger value="tools" className="data-[state=active]:bg-primary">
              <Brain className="h-4 w-4 mr-2" />
              AI Tools
            </TabsTrigger>
            <TabsTrigger value="incidents" className="data-[state=active]:bg-primary">
              <AlertCircle className="h-4 w-4 mr-2" />
              Incidents
            </TabsTrigger>
            <TabsTrigger value="policies" className="data-[state=active]:bg-primary">
              <Settings className="h-4 w-4 mr-2" />
              Policies
            </TabsTrigger>
            <TabsTrigger value="alert-settings" className="data-[state=active]:bg-primary">
              <Bell className="h-4 w-4 mr-2" />
              Alerts
            </TabsTrigger>
            <TabsTrigger value="agent" className="data-[state=active]:bg-primary">
              <Server className="h-4 w-4 mr-2" />
              Agent Setup
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {dashboard && (
              <div className="grid md:grid-cols-2 gap-6">
                {/* Usage by Tool */}
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle>Top AI Tools Used</CardTitle>
                    <CardDescription>Most frequently detected AI services</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {dashboard.usage_by_tool.length > 0 ? (
                      <div className="space-y-3">
                        {dashboard.usage_by_tool.map(([tool, count], i) => (
                          <div key={i} className="flex items-center justify-between">
                            <span className="text-sm">{tool}</span>
                            <Badge variant="outline">{count} events</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No AI usage detected yet. Analyze network logs to track AI tool usage.</p>
                    )}
                  </CardContent>
                </Card>

                {/* Usage by Risk */}
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle>Usage by Risk Level</CardTitle>
                    <CardDescription>AI tool usage categorized by risk</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(dashboard.usage_by_risk).map(([level, count]) => (
                        <div key={level} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge className={getRiskBadgeColor(level)}>{level.toUpperCase()}</Badge>
                          </div>
                          <span className="font-medium">{count} events</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Compliance Report Preview */}
                {complianceReport && (
                  <Card className="bg-card border-border md:col-span-2">
                    <CardHeader>
                      <CardTitle>AI Governance Compliance Report</CardTitle>
                      <CardDescription>Generated {new Date(complianceReport.report_date).toLocaleString()}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-3 gap-6">
                        <div className="space-y-2">
                          <p className="text-sm text-muted-foreground">GDPR Status</p>
                          <Badge className={complianceReport.regulatory_status.gdpr === "Compliant" ? "bg-emerald-500/20 text-emerald-400" : "bg-yellow-500/20 text-yellow-400"}>
                            {complianceReport.regulatory_status.gdpr}
                          </Badge>
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm text-muted-foreground">HIPAA Status</p>
                          <Badge className={complianceReport.regulatory_status.hipaa === "Compliant" ? "bg-emerald-500/20 text-emerald-400" : "bg-yellow-500/20 text-yellow-400"}>
                            {complianceReport.regulatory_status.hipaa}
                          </Badge>
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm text-muted-foreground">EU AI Act Status</p>
                          <Badge className={complianceReport.regulatory_status.eu_ai_act === "Low Risk" ? "bg-emerald-500/20 text-emerald-400" : "bg-yellow-500/20 text-yellow-400"}>
                            {complianceReport.regulatory_status.eu_ai_act}
                          </Badge>
                        </div>
                      </div>
                      {complianceReport.risk_assessment.recommendations.length > 0 && (
                        <div className="mt-6 p-4 bg-muted/30 rounded-sm">
                          <p className="font-medium mb-2">Recommendations:</p>
                          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                            {complianceReport.risk_assessment.recommendations.map((rec, i) => (
                              <li key={i}>{rec}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>

          {/* Monitor Logs Tab */}
          <TabsContent value="monitor" className="space-y-6">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle>Analyze Network Logs for AI Usage</CardTitle>
                <CardDescription>
                  Paste your network/proxy logs to detect AI tool usage. Supports access logs, firewall logs, and proxy logs.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  placeholder={`Paste your network logs here...

Example:
192.168.1.50 - - [15/Mar/2024:10:30:00] "POST https://api.openai.com/v1/chat/completions HTTP/1.1" 200 1234
192.168.1.51 - - [15/Mar/2024:10:31:00] "GET https://claude.ai/chat HTTP/1.1" 200 5678
192.168.1.52 - - [15/Mar/2024:10:32:00] "POST https://api.anthropic.com/v1/messages HTTP/1.1" 200 9012`}
                  className="min-h-[200px] font-mono text-sm"
                  value={logContent}
                  onChange={(e) => setLogContent(e.target.value)}
                />
                <Button onClick={analyzeAILogs} disabled={analyzingLogs}>
                  {analyzingLogs ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Search className="mr-2 h-4 w-4" />
                      Analyze Logs
                    </>
                  )}
                </Button>

                {logAnalysisResult && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-6 space-y-4"
                  >
                    <div className={`p-4 rounded-sm border ${
                      logAnalysisResult.summary.status === 'critical' ? 'bg-red-500/10 border-red-500/30' :
                      logAnalysisResult.summary.status === 'warning' ? 'bg-yellow-500/10 border-yellow-500/30' :
                      'bg-emerald-500/10 border-emerald-500/30'
                    }`}>
                      <p className="font-medium">{logAnalysisResult.summary.message}</p>
                      <div className="flex gap-4 mt-2 text-sm">
                        <span>Tools: {logAnalysisResult.tools_detected.join(", ") || "None"}</span>
                        <span>High Risk: {logAnalysisResult.high_risk_usage}</span>
                        <span>Violations: {logAnalysisResult.policy_violations}</span>
                      </div>
                    </div>

                    {logAnalysisResult.events.length > 0 && (
                      <div className="space-y-2">
                        <p className="font-medium">Detected Events:</p>
                        {logAnalysisResult.events.map((event, i) => (
                          <div key={i} className="p-3 bg-muted/30 rounded-sm flex items-center justify-between">
                            <div>
                              <p className="font-medium">{event.tool_name}</p>
                              <p className="text-sm text-muted-foreground">IP: {event.user_ip} | Category: {event.category}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={getRiskBadgeColor(event.risk_level)}>{event.risk_level}</Badge>
                              {event.policy_violated && (
                                <Badge className="bg-red-500/20 text-red-400">Violation</Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Data Scanner Tab */}
          <TabsContent value="data-check" className="space-y-6">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle>Sensitive Data Scanner</CardTitle>
                <CardDescription>
                  Check text for sensitive data before sending to AI tools. Detects SSN, credit cards, PHI, API keys, and more.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  placeholder={`Paste text you want to check for sensitive data...

This will detect:
- Social Security Numbers
- Credit Card Numbers  
- Medical Record Info (PHI)
- API Keys & Tokens
- Passwords
- Email Addresses
- And more...`}
                  className="min-h-[200px]"
                  value={textToCheck}
                  onChange={(e) => setTextToCheck(e.target.value)}
                />
                <Button onClick={checkSensitiveData} disabled={checkingData}>
                  {checkingData ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Scanning...
                    </>
                  ) : (
                    <>
                      <Lock className="mr-2 h-4 w-4" />
                      Scan for Sensitive Data
                    </>
                  )}
                </Button>

                {sensitiveDataResult && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-6 space-y-4"
                  >
                    <div className={`p-4 rounded-sm border ${
                      sensitiveDataResult.overall_risk === 'critical' ? 'bg-red-500/10 border-red-500/30' :
                      sensitiveDataResult.overall_risk === 'high' ? 'bg-orange-500/10 border-orange-500/30' :
                      sensitiveDataResult.overall_risk === 'medium' ? 'bg-yellow-500/10 border-yellow-500/30' :
                      'bg-emerald-500/10 border-emerald-500/30'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        {sensitiveDataResult.sensitive_data_found ? (
                          <AlertTriangle className="h-5 w-5 text-red-500" />
                        ) : (
                          <CheckCircle className="h-5 w-5 text-emerald-500" />
                        )}
                        <span className="font-medium">
                          {sensitiveDataResult.sensitive_data_found 
                            ? `Found ${sensitiveDataResult.total_findings} types of sensitive data!`
                            : "No sensitive data detected"}
                        </span>
                      </div>
                      <Badge className={getRiskBadgeColor(sensitiveDataResult.overall_risk)}>
                        {sensitiveDataResult.overall_risk.toUpperCase()} RISK
                      </Badge>
                    </div>

                    {sensitiveDataResult.findings.length > 0 && (
                      <div className="space-y-2">
                        <p className="font-medium">Findings:</p>
                        {sensitiveDataResult.findings.map((finding, i) => (
                          <div key={i} className="p-3 bg-muted/30 rounded-sm">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium">{finding.name}</span>
                              <Badge className={getRiskBadgeColor(finding.risk)}>{finding.risk}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              Found {finding.count} instance(s) | Regulations: {finding.regulations.join(", ")}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}

                    {sensitiveDataResult.recommendations.length > 0 && (
                      <div className="p-4 bg-primary/10 rounded-sm">
                        <p className="font-medium mb-2">Recommendations:</p>
                        <ul className="list-disc list-inside space-y-1 text-sm">
                          {sensitiveDataResult.recommendations.map((rec, i) => (
                            <li key={i}>{rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </motion.div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* AI Tools Tab */}
          <TabsContent value="tools" className="space-y-6">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle>Known AI Tools Risk Assessment</CardTitle>
                <CardDescription>
                  Comprehensive database of AI tools with risk levels and compliance information
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {tools.map((tool, i) => (
                    <div key={i} className="p-4 bg-muted/30 rounded-sm">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <Brain className="h-5 w-5 text-primary" />
                          <span className="font-medium">{tool.name}</span>
                        </div>
                        <Badge className={getRiskBadgeColor(tool.risk_level)}>
                          {tool.risk_level.toUpperCase()} RISK
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{tool.description}</p>
                      <div className="flex flex-wrap gap-2 text-xs">
                        <Badge variant="outline">{tool.category}</Badge>
                        <Badge variant="outline" className={tool.gdpr_compliant ? "text-emerald-400" : "text-red-400"}>
                          {tool.gdpr_compliant ? "GDPR Compliant" : "GDPR Review Needed"}
                        </Badge>
                        <Badge variant="outline" className={tool.hipaa_compliant ? "text-emerald-400" : "text-red-400"}>
                          {tool.hipaa_compliant ? "HIPAA Compliant" : "Not HIPAA Compliant"}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        Data Retention: {tool.data_retention}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Incidents Tab */}
          <TabsContent value="incidents" className="space-y-6">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle>AI Security Incidents</CardTitle>
                <CardDescription>
                  Policy violations, sensitive data incidents, and high-risk AI usage
                </CardDescription>
              </CardHeader>
              <CardContent>
                {incidents.length > 0 ? (
                  <div className="space-y-3">
                    {incidents.map((incident, i) => (
                      <div key={i} className="p-4 bg-muted/30 rounded-sm border-l-4 border-l-red-500">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">{incident.tool_name}</span>
                          <div className="flex gap-2">
                            <Badge className={getRiskBadgeColor(incident.risk_level)}>
                              {incident.risk_level}
                            </Badge>
                            {incident.policy_violated && (
                              <Badge className="bg-red-500/20 text-red-400">Policy Violation</Badge>
                            )}
                            {incident.sensitive_data_detected && (
                              <Badge className="bg-orange-500/20 text-orange-400">Sensitive Data</Badge>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          IP: {incident.user_ip} | Time: {new Date(incident.timestamp).toLocaleString()}
                        </p>
                        {incident.violations && incident.violations.length > 0 && (
                          <div className="mt-2 text-sm text-red-400">
                            {incident.violations.map((v, j) => (
                              <p key={j}>{v.message}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No incidents detected. Analyze network logs to monitor AI usage.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Policies Tab */}
          <TabsContent value="policies" className="space-y-6">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle>AI Usage Policies</CardTitle>
                <CardDescription>
                  Define rules for allowed AI tools, risk thresholds, and data restrictions
                </CardDescription>
              </CardHeader>
              <CardContent>
                {policies.map((policy, i) => (
                  <div key={i} className="p-4 bg-muted/30 rounded-sm mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{policy.name}</span>
                      {policy.active && <Badge className="bg-emerald-500/20 text-emerald-400">Active</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">{policy.description}</p>
                    <div className="grid md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Max Risk Level:</p>
                        <Badge className={getRiskBadgeColor(policy.max_risk_level)}>{policy.max_risk_level}</Badge>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Blocked Tools:</p>
                        <p>{policy.blocked_tools?.length > 0 ? policy.blocked_tools.join(", ") : "None"}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Blocked Categories:</p>
                        <p>{policy.blocked_categories?.length > 0 ? policy.blocked_categories.join(", ") : "None"}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Data Restrictions:</p>
                        <p>{policy.data_restrictions?.length > 0 ? policy.data_restrictions.join(", ") : "None"}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Alert Settings Tab */}
          <TabsContent value="alert-settings" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Alert Configuration */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="h-5 w-5 text-primary" />
                    Alert Configuration
                  </CardTitle>
                  <CardDescription>
                    Configure real-time alerts when PHI/PII is detected being sent to AI services
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Email Alerts Toggle */}
                  <div className="flex items-center justify-between p-4 bg-muted/30 rounded-sm">
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-primary" />
                      <div>
                        <p className="font-medium">Email Alerts</p>
                        <p className="text-sm text-muted-foreground">Send email when sensitive data is detected</p>
                      </div>
                    </div>
                    <Button
                      variant={alertSettings.email_alerts_enabled ? "default" : "outline"}
                      size="sm"
                      data-testid="alert-toggle-email"
                      onClick={() => setAlertSettings({...alertSettings, email_alerts_enabled: !alertSettings.email_alerts_enabled})}
                    >
                      {alertSettings.email_alerts_enabled ? (
                        <><ToggleRight className="h-4 w-4 mr-1" /> ON</>
                      ) : (
                        <><ToggleLeft className="h-4 w-4 mr-1" /> OFF</>
                      )}
                    </Button>
                  </div>

                  {/* Auto-Block Toggle */}
                  <div className="flex items-center justify-between p-4 bg-red-500/10 border border-red-500/30 rounded-sm">
                    <div className="flex items-center gap-3">
                      <Shield className="h-5 w-5 text-red-500" />
                      <div>
                        <p className="font-medium text-red-400">Auto-Block PHI/PII</p>
                        <p className="text-sm text-muted-foreground">Automatically block requests containing critical data</p>
                      </div>
                    </div>
                    <Button
                      variant={alertSettings.auto_block_enabled ? "destructive" : "outline"}
                      size="sm"
                      data-testid="alert-toggle-auto-block"
                      onClick={() => setAlertSettings({...alertSettings, auto_block_enabled: !alertSettings.auto_block_enabled})}
                    >
                      {alertSettings.auto_block_enabled ? (
                        <><ToggleRight className="h-4 w-4 mr-1" /> ON</>
                      ) : (
                        <><ToggleLeft className="h-4 w-4 mr-1" /> OFF</>
                      )}
                    </Button>
                  </div>

                  {/* Alert Email */}
                  <div className="space-y-2">
                    <Label>Alert Email (optional)</Label>
                    <Input
                      type="email"
                      placeholder="Override default email for alerts"
                      value={alertSettings.alert_email || ""}
                      data-testid="alert-email-input"
                      onChange={(e) => setAlertSettings({...alertSettings, alert_email: e.target.value})}
                    />
                    <p className="text-xs text-muted-foreground">Leave blank to use your account email</p>
                  </div>

                  {/* Save Button */}
                  <Button onClick={saveAlertSettings} disabled={savingSettings} className="w-full" data-testid="save-alert-settings-btn">
                    {savingSettings ? "Saving..." : "Save Alert Settings"}
                  </Button>
                </CardContent>
              </Card>

              {/* Data Types to Alert On */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle>Alert Triggers</CardTitle>
                  <CardDescription>
                    Select which types of sensitive data should trigger alerts
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* PHI */}
                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-sm">
                    <div>
                      <p className="font-medium">PHI (HIPAA)</p>
                      <p className="text-xs text-muted-foreground">Medical records, diagnoses, patient IDs</p>
                    </div>
                    <Button
                      variant={alertSettings.alert_on_phi ? "default" : "outline"}
                      size="sm"
                      onClick={() => setAlertSettings({...alertSettings, alert_on_phi: !alertSettings.alert_on_phi})}
                    >
                      {alertSettings.alert_on_phi ? "ON" : "OFF"}
                    </Button>
                  </div>

                  {/* PII */}
                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-sm">
                    <div>
                      <p className="font-medium">PII (GDPR/CCPA)</p>
                      <p className="text-xs text-muted-foreground">SSN, names, addresses, DOB, contact info</p>
                    </div>
                    <Button
                      variant={alertSettings.alert_on_pii ? "default" : "outline"}
                      size="sm"
                      onClick={() => setAlertSettings({...alertSettings, alert_on_pii: !alertSettings.alert_on_pii})}
                    >
                      {alertSettings.alert_on_pii ? "ON" : "OFF"}
                    </Button>
                  </div>

                  {/* Credentials */}
                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-sm">
                    <div>
                      <p className="font-medium">Credentials</p>
                      <p className="text-xs text-muted-foreground">API keys, passwords, tokens</p>
                    </div>
                    <Button
                      variant={alertSettings.alert_on_credentials ? "default" : "outline"}
                      size="sm"
                      onClick={() => setAlertSettings({...alertSettings, alert_on_credentials: !alertSettings.alert_on_credentials})}
                    >
                      {alertSettings.alert_on_credentials ? "ON" : "OFF"}
                    </Button>
                  </div>

                  {alertSettings.auto_block_enabled && (
                    <div className="pt-4 border-t border-border">
                      <p className="text-sm font-medium mb-3 text-red-400">Auto-Block Settings</p>
                      <div className="space-y-2">
                        {[
                          { key: "auto_block_ssn", label: "Social Security Numbers" },
                          { key: "auto_block_medical_records", label: "Medical Records/Diagnoses" },
                          { key: "auto_block_credit_cards", label: "Credit Card Numbers" },
                          { key: "auto_block_api_keys", label: "API Keys/Passwords" }
                        ].map((item) => (
                          <div key={item.key} className="flex items-center justify-between p-2 bg-red-500/5 rounded-sm">
                            <span className="text-sm">{item.label}</span>
                            <Button
                              variant={alertSettings[item.key] ? "destructive" : "outline"}
                              size="sm"
                              className="h-6 text-xs"
                              onClick={() => setAlertSettings({...alertSettings, [item.key]: !alertSettings[item.key]})}
                            >
                              {alertSettings[item.key] ? "BLOCK" : "ALLOW"}
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Recent Alerts */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                  Recent Alerts
                </CardTitle>
                <CardDescription>
                  PHI/PII detection alerts from the last 30 days
                </CardDescription>
              </CardHeader>
              <CardContent>
                {alerts.length > 0 ? (
                  <div className="space-y-3">
                    {alerts.slice(0, 10).map((alert, i) => (
                      <div key={i} className="p-4 bg-red-500/10 border border-red-500/30 rounded-sm">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <AlertCircle className="h-4 w-4 text-red-500" />
                            <span className="font-medium text-red-400">{alert.event?.tool_name || "AI Service"}</span>
                          </div>
                          <Badge className="bg-red-500/20 text-red-400">{alert.severity?.toUpperCase()}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">
                          Sensitive data types: {alert.sensitive_data_types?.join(", ") || "Unknown"}
                        </p>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>From: {alert.event?.user_ip || "Unknown"}</span>
                          <span>{new Date(alert.created_at).toLocaleString()}</span>
                        </div>
                        {alert.auto_blocked && (
                          <Badge className="mt-2 bg-red-500/30 text-red-300">AUTO-BLOCKED</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Bell className="h-12 w-12 mx-auto mb-4 opacity-20" />
                    <p>No alerts yet</p>
                    <p className="text-sm">Alerts will appear here when PHI/PII is detected</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Agent Setup Tab */}
          <TabsContent value="agent" className="space-y-6">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Server className="h-5 w-5 text-primary" />
                  AI Governance Agent
                </CardTitle>
                <CardDescription>
                  Deploy the AI Governance agent on your network to automatically monitor all employee AI tool usage and detect sensitive data leaks in real-time.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Download Section */}
                <div className="p-4 bg-primary/10 border border-primary/30 rounded-sm">
                  <h3 className="font-medium mb-2 flex items-center gap-2">
                    <Download className="h-4 w-4" />
                    Download Agent
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Download the Python agent script that monitors network traffic for AI service usage.
                  </p>
                  <Button 
                    onClick={() => window.open(`${BACKEND_URL}/api/agent/download/ai-governance`, '_blank')}
                    variant="outline"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Download ai_governance_agent.py
                  </Button>
                </div>

                {/* Installation Steps */}
                <div className="space-y-4">
                  <h3 className="font-medium flex items-center gap-2">
                    <Terminal className="h-4 w-4" />
                    Installation Guide
                  </h3>
                  
                  <div className="space-y-3">
                    <div className="p-3 bg-muted/30 rounded-sm">
                      <p className="font-medium text-sm mb-1">Step 1: Install Dependencies</p>
                      <code className="text-xs bg-black/30 p-2 rounded block">
                        pip install requests mitmproxy
                      </code>
                    </div>

                    <div className="p-3 bg-muted/30 rounded-sm">
                      <p className="font-medium text-sm mb-1">Step 2: Configure Environment Variables</p>
                      <code className="text-xs bg-black/30 p-2 rounded block whitespace-pre">
{`export SECURESCAN_API_URL="${BACKEND_URL}"
export SECURESCAN_API_KEY="your-api-key-here"
export SECURESCAN_ORG_ID="your-org-id"`}
                      </code>
                      <p className="text-xs text-muted-foreground mt-2">
                        Generate an API key from Settings → API Keys
                      </p>
                    </div>

                    <div className="p-3 bg-muted/30 rounded-sm">
                      <p className="font-medium text-sm mb-1">Step 3: Run the Agent</p>
                      <div className="space-y-2">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Log Monitoring Mode (analyze existing proxy/firewall logs):</p>
                          <code className="text-xs bg-black/30 p-2 rounded block">
                            python3 ai_governance_agent.py --mode logs --watch /var/log/squid/access.log
                          </code>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Proxy Mode (real-time interception):</p>
                          <code className="text-xs bg-black/30 p-2 rounded block">
                            sudo python3 ai_governance_agent.py --mode proxy --port 8888
                          </code>
                        </div>
                      </div>
                    </div>

                    <div className="p-3 bg-muted/30 rounded-sm">
                      <p className="font-medium text-sm mb-1">Step 4: Run as Service (Optional)</p>
                      <code className="text-xs bg-black/30 p-2 rounded block whitespace-pre">
{`# Create systemd service
sudo nano /etc/systemd/system/securescan-ai-gov.service

# Enable and start
sudo systemctl enable securescan-ai-gov
sudo systemctl start securescan-ai-gov`}
                      </code>
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div className="grid md:grid-cols-2 gap-4 mt-6">
                  <div className="p-4 bg-muted/30 rounded-sm">
                    <h4 className="font-medium flex items-center gap-2 mb-2">
                      <Eye className="h-4 w-4 text-primary" />
                      AI Service Detection
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Automatically detects usage of 20+ AI services including ChatGPT, Claude, Copilot, Gemini, Midjourney, and more.
                    </p>
                  </div>

                  <div className="p-4 bg-muted/30 rounded-sm">
                    <h4 className="font-medium flex items-center gap-2 mb-2">
                      <Lock className="h-4 w-4 text-orange-500" />
                      Sensitive Data Detection
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Scans for SSN, credit cards, medical records, API keys, passwords, and other PHI/PII before it reaches AI services.
                    </p>
                  </div>

                  <div className="p-4 bg-muted/30 rounded-sm">
                    <h4 className="font-medium flex items-center gap-2 mb-2">
                      <Shield className="h-4 w-4 text-red-500" />
                      Auto-Block Capability
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Optionally block requests containing critical sensitive data before they reach non-compliant AI services.
                    </p>
                  </div>

                  <div className="p-4 bg-muted/30 rounded-sm">
                    <h4 className="font-medium flex items-center gap-2 mb-2">
                      <TrendingUp className="h-4 w-4 text-emerald-500" />
                      Real-Time Reporting
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Reports all AI usage events back to your AI Governance Office dashboard for centralized monitoring and compliance.
                    </p>
                  </div>
                </div>

                {/* HIPAA Note */}
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-sm">
                  <h4 className="font-medium text-red-400 flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-4 w-4" />
                    HIPAA Compliance Notice
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Most AI services are <strong className="text-red-400">NOT HIPAA compliant</strong>. This agent helps you monitor and prevent unauthorized disclosure of Protected Health Information (PHI) to AI services. For healthcare organizations, we recommend enabling the auto-block feature to prevent PHI leaks.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
